<template>
    <crud-create :open="open"
                 :fullscreen="true"
                 :loading="loading"
                 :title="title"
                 :errorMessage="errorMessage"
                 @create="create"
                 @close="$emit('close')"
    >
        <movimiento-form-compra ref="form" 
            v-model="form" 
            @guardarItemCarrito="guardarItemCarrito"
            @eliminarItemCarrito="eliminarItemCarrito"
            @actualizarDescuento="actualizarDescuento"
            @actualizarRecargo="actualizarRecargo"
            @actualizarMediosDePago="actualizarMediosDePago"
            @actualizarPagoRecibido="actualizarPagoRecibido"
            @actualizarListaPreciosVenta="actualizarListaPreciosVenta"
            :input-errors="inputErrors" />
    </crud-create>
</template>

<script>

    import MovimientoProvider from "../../../providers/MovimientoProvider";
    
    import {CrudCreate, ClientError} from '@dracul/common-frontend'
    
    import MovimientoFormCompra from "../MovimientoForm/MovimientoFormCompra.vue";

    export default {
        name: "MovimientoCreate",
         
        components: { MovimientoFormCompra, CrudCreate },
        
        props:{
          open: {type: Boolean, default: true}
        },
        
        data() {
            return {
                title: 'movimiento.creating',
                errorMessage: '',
                inputErrors: {},
                loading: false,
                form: {
                    productos: [],
                    cantidad: null,
                    fecha: null,
                },
                itemsCarrito: [],
                descuento: null,
                mediosDePago: [],
                dineroRecibido: 0,
                cambioADevolver: 0,
                recargo: null,
                recargoId: null,
                listaPreciosVentaId: null
            }
        },
        
        methods: {
            async create() {
                if (this.itemsCarrito.length > 0) {
                    this.loading = true

                    // Calcular precio total base (sin ofertas) para preview
                    // El backend calculará el precio final con ofertas aplicadas
                    let precioTotalBase = 0;
                    for (const item of this.itemsCarrito) {
                        const pv = (item.producto.precio ?? item.producto.precioVenta) || 0;
                        precioTotalBase += (pv * item.cantidad);
                    }

                    const descuento = this.descuento != null ? parseFloat(this.descuento) : 0
                    const recargo = this.recargo != null ? parseFloat(this.recargo) : 0
                    const recargoId = this.recargoId || undefined

                    // Precio total estimado (el backend recalculará con ofertas)
                    let precioTotalEstimado = precioTotalBase * (1 - (descuento / 100)) * (1 + (recargo / 100))

                    // Preparar carrito con precioBase para que el backend pueda aplicar ofertas
                    let carrito = JSON.parse(JSON.stringify(this.itemsCarrito));
                    
                    for (const item of carrito) {
                        const precioBase = (item.producto.precio ?? item.producto.precioVenta) || 0;
                        
                        // Asignar precioBase (necesario para que el backend calcule ofertas)
                        item.precioBase = precioBase;
                        // precioTotal inicial será el precio sin ofertas, el backend lo actualizará
                        item.precioTotal = precioBase * (item.cantidad || 0);
                        
                        // Eliminar campos que no son parte del schema GraphQL
                        delete item.precio;
                        delete item.ofertaAplicada; // Información solo para UI, no para backend
                        
                        // Convertir producto a solo el ID
                        item.producto = item.producto.id;
                    }

                    const mediosNormalizados = (this.mediosDePago || []).map((p) => ({
                        medio: p.medio || 'Efectivo',
                        monto: Number(p.monto) || 0,
                    }));
                    const conMonto = mediosNormalizados.filter((p) => p.monto > 0);
                    // Con una sola fila de medio de pago el formulario no muestra el campo monto (queda 0);
                    // hay que enviar el medio elegido con el total, no forzar Efectivo.
                    let mediosDePagoInput;
                    if (conMonto.length > 0) {
                        mediosDePagoInput = conMonto;
                    } else if (mediosNormalizados.length === 1) {
                        mediosDePagoInput = [
                            {
                                medio: mediosNormalizados[0].medio || 'Efectivo',
                                monto: precioTotalEstimado,
                            },
                        ];
                    } else {
                        mediosDePagoInput = [{ medio: 'Efectivo', monto: precioTotalEstimado }];
                    }

                    const input = {
                        itemsCarrito: carrito,
                        precioTotal: precioTotalEstimado,
                        descuento: descuento,
                        mediosDePago: mediosDePagoInput,
                        recargo: recargo,
                        recargoId: recargoId,
                        deposito: this.depositoId || undefined,
                        dineroRecibido: this.dineroRecibido != null ? Number(this.dineroRecibido) : undefined,
                        cambioADevolver: this.cambioADevolver != null ? Number(this.cambioADevolver) : undefined,
                        listaPreciosVentaId: this.listaPreciosVentaId || undefined,
                    };

                    console.log('[MovimientoCreate] createMovimiento variables:', {
                        mediosDePago: input.mediosDePago,
                        itemsCarritoLength: input.itemsCarrito?.length,
                        recargoId: input.recargoId,
                    });

                    if (precioTotalEstimado >= 0) {
                        MovimientoProvider.createMovimiento(input).then(r => {
                            if (r) {
                                this.$emit('itemCreated', r.data.movimientoCreate)
                                this.$emit('close')
                            }
                        }).catch(error => {
                             let clientError = new ClientError(error)
                             this.inputErrors = clientError.inputErrors
                             this.errorMessage = clientError.i18nMessage
                        }).finally(() => this.loading = false)
                    }
                }
            },
            guardarItemCarrito(item) {
                // Evitar duplicar el mismo producto en el carrito del create
                const exists = this.itemsCarrito.some(
                    (it) => it.producto && item.producto && it.producto.id === item.producto.id
                )

                if (!exists) {
                    this.itemsCarrito.push(item);
                }
            },
            eliminarItemCarrito(item) {
                const index = this.itemsCarrito.findIndex(
                    (it) => it.producto && item.producto && it.producto.id === item.producto.id
                )
                if (index > -1) this.itemsCarrito.splice(index, 1);
            },
            actualizarDescuento(descuento) {
                this.descuento = descuento;
            },
            actualizarRecargo(payload) {
                if (payload && typeof payload === 'object' && 'recargo' in payload) {
                    this.recargo = payload.recargo;
                    this.recargoId = payload.recargoId != null ? payload.recargoId : null;
                } else {
                    this.recargo = payload != null ? parseFloat(payload) : 0;
                    this.recargoId = null;
                }
            },
            actualizarMediosDePago(mediosDePago) {
                this.mediosDePago = Array.isArray(mediosDePago) ? mediosDePago : [];
            },
            actualizarPagoRecibido(payload) {
                if (payload && typeof payload === 'object') {
                    if ('dineroRecibido' in payload) this.dineroRecibido = payload.dineroRecibido;
                    if ('cambioADevolver' in payload) this.cambioADevolver = payload.cambioADevolver;
                }
            },
            actualizarListaPreciosVenta(listaPreciosVentaId) {
                this.listaPreciosVentaId = listaPreciosVentaId || null;
            }

        },
    }
</script>

<style scoped>

</style>

